'use client';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getLicense } from '@/lib/api/license';
import { getLocal, setLocal } from '@/lib/storage';

interface LicenseType {
  license: {
    activated_on: string;
    created_on: string;
    expires: boolean;
    length: number;
    license_type: string;
    license_variant: string;
    remaining_time: number;
    renews_on?: string;
    plan_id?: string;
    subscription_id?: string;
  };
  license_use: {
    trial: boolean;
  };
  //TODO: Add map_promo_license to the license type
  lastFetched?: number;
}

interface LicenseContextType {
  license: LicenseType | null;
  updateLicense: (license: LicenseType | null) => void;
}

const LicenseContext = createContext<LicenseContextType | undefined>(undefined);
const LOCAL_STORAGE_KEY = 'userLicense';

export function LicenseProvider({ children }: { children: ReactNode }) {
  const { sessionId } = useAuth();
  const [license, setLicense] = useState<LicenseType | null>(null);

  // Load license from localStorage on mount
  useEffect(() => {
    const saved = getLocal<LicenseType | null>(LOCAL_STORAGE_KEY, null);
    setLicense(saved);
  }, []);

  // Save license to localStorage when it changes
  useEffect(() => {
    setLocal(LOCAL_STORAGE_KEY, license);
  }, [license]);

  // Fetch license on mount or when sessionId changes
  useEffect(() => {
    if (sessionId) {
      getLicense(sessionId).then(response => {
        if (response.statusCode === 200) {
          setLicense({ ...response.body, lastFetched: Date.now() });
        }
      });
    } else {
      setLicense(null);
    }
  }, [sessionId]);

  const updateLicense = (newLicense: LicenseType | null) => {
    setLicense(newLicense ? { ...newLicense, lastFetched: Date.now() } : null);
    // Optionally persist to localStorage or server
  };

  return (
    <LicenseContext.Provider value={{ license, updateLicense }}>
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense() {
  const context = useContext(LicenseContext);
  if (!context) throw new Error('useLicense must be used within a LicenseProvider');
  return context;
} 